import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5555';

interface QuickTestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'speech' | 'ielts' | 'interview';
type StepType = 'select' | 'record' | 'result' | 'email';

interface TabData {
  id: TabType;
  label: string;
  icon: string;
  description: string;
  prompts: { id: string; label: string; text: string }[];
  color: string;
}

const tabs: TabData[] = [
  {
    id: 'speech',
    label: 'Speech Practice',
    icon: '🎤',
    description: 'Practice pronunciation with sample texts',
    prompts: [
      { id: 'pangram', label: 'Classic Pangram', text: 'The quick brown fox jumps over the lazy dog.' },
      { id: 'seashells', label: 'Tongue Twister', text: 'She sells seashells by the seashore.' },
      { id: 'weather', label: 'TH Sounds', text: 'I think that the weather will be better on Thursday.' },
      { id: 'directions', label: 'Conversational', text: 'Could you please tell me how to get to the train station?' },
    ],
    color: 'blue',
  },
  {
    id: 'ielts',
    label: 'Quick IELTS',
    icon: '📝',
    description: 'Try IELTS Speaking Part 2 questions',
    prompts: [
      { id: 'place', label: 'Favorite Place', text: 'Describe your favorite place to relax. What do you do there and why do you enjoy it?' },
      { id: 'person', label: 'Influential Person', text: 'Describe a person who has influenced you. How did they influence you and why?' },
      { id: 'event', label: 'Memorable Event', text: 'Describe a memorable event from your childhood. What happened and why do you remember it?' },
      { id: 'skill', label: 'Useful Skill', text: 'Describe a skill you would like to learn. Why is it useful and how would you learn it?' },
    ],
    color: 'emerald',
  },
  {
    id: 'interview',
    label: 'Quick Interview',
    icon: '💼',
    description: 'Practice common interview questions',
    prompts: [
      { id: 'intro', label: 'Tell Me About Yourself', text: 'Tell me about yourself and why you are interested in this opportunity.' },
      { id: 'strength', label: 'Greatest Strength', text: 'What is your greatest strength and how has it helped you professionally?' },
      { id: 'challenge', label: 'Overcome Challenge', text: 'Describe a challenging situation at work and how you handled it.' },
    ],
    color: 'purple',
  },
];

const QuickTestModal: React.FC<QuickTestModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('speech');
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [step, setStep] = useState<StepType>('select');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const navigate = useNavigate();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const activeTabData = tabs.find((t) => t.id === activeTab)!;
  const selectedPrompt = activeTabData.prompts.find((p) => p.id === selectedPromptId) || activeTabData.prompts[0];

  // Set default prompt when tab changes
  useEffect(() => {
    setSelectedPromptId(activeTabData.prompts[0].id);
  }, [activeTab, activeTabData.prompts]);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 60) {
            // Auto-stop after 60 seconds
            if (mediaRecorderRef.current) {
              mediaRecorderRef.current.stop();
              setIsRecording(false);
            }
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    setAnalysisError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setAnalysisError('Please allow microphone access to use this feature.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const analyzeRecording = async () => {
    if (!audioBlob) return;

    setIsAnalyzing(true);
    try {
      // Step 1: Transcribe audio to text
      const transcribeFormData = new FormData();
      transcribeFormData.append('audio', audioBlob, 'recording.webm');

      const transcribeResponse = await fetch(`${API_BASE}/api/transcribe`, {
        method: 'POST',
        body: transcribeFormData,
      });

      if (!transcribeResponse.ok) {
        // The API explains what to do when the recording itself was the
        // problem — too short, no speech. Prefer that over a generic failure.
        const details = await transcribeResponse.json().catch(() => null);
        throw new Error(details?.error || 'Transcription failed');
      }

      const transcribeResult = await transcribeResponse.json();
      const transcribedText = transcribeResult.text || '';

      if (!transcribedText.trim()) {
        throw new Error('Could not understand the audio. Please try again.');
      }

      // Step 2: Analyze based on test type
      let analysisData;

      if (activeTab === 'speech') {
        // For speech practice, use pronunciation assessment
        const proFormData = new FormData();
        proFormData.append('audio', audioBlob, 'recording.webm');
        proFormData.append('targetText', selectedPrompt.text);

        try {
          const proResponse = await fetch(`${API_BASE}/api/transcribe/pro`, {
            method: 'POST',
            body: proFormData,
          });

          if (proResponse.ok) {
            const proResult = await proResponse.json();
            analysisData = {
              type: 'speech',
              overallScore: Math.round(proResult.proAnalysis?.pronunciationScore || 75),
              pronunciation: Math.round(proResult.proAnalysis?.accuracy || 75),
              fluency: Math.round(proResult.proAnalysis?.fluency || 75),
              feedback: generateSpeechFeedback(proResult.proAnalysis),
              transcribedText,
            };
          }
        } catch (proErr) {
          console.error('Pro analysis failed, using basic result:', proErr);
        }

        // Fallback: use basic transcription comparison if pro analysis unavailable
        if (!analysisData) {
          const targetWords = selectedPrompt.text.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/);
          const spokenWords = transcribedText.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/);
          const matched = targetWords.filter(w => spokenWords.includes(w)).length;
          const accuracy = Math.round((matched / Math.max(targetWords.length, 1)) * 100);
          analysisData = {
            type: 'speech',
            overallScore: accuracy,
            pronunciation: accuracy,
            fluency: Math.min(100, Math.round(accuracy * 1.05)),
            feedback: generateSpeechFeedback({ pronunciationScore: accuracy }),
            transcribedText,
          };
        }
      } else if (activeTab === 'interview') {
        // For interview, evaluate the answer content
        const evalResponse = await fetch(`${API_BASE}/api/interview/evaluate-answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobTitle: 'General Position',
            question: selectedPrompt.text,
            answer: transcribedText,
          }),
        });

        if (evalResponse.ok) {
          const evalResult = await evalResponse.json();
          analysisData = {
            type: 'interview',
            overallScore: evalResult.overallScore || 70,
            pronunciation: 75, // Would need separate pronunciation analysis
            fluency: 70,
            contentRelevance: evalResult.relevanceScore || evalResult.overallScore || 70,
            confidence: evalResult.confidenceScore || 68,
            feedback: evalResult.feedback || evalResult.summary || 'Your answer was analyzed.',
            transcribedText,
            answerAnalysis: {
              relevance: evalResult.relevanceFeedback || 'Your answer addressed the question.',
              structure: evalResult.structureFeedback || 'Consider using the STAR method for better structure.',
              improvements: evalResult.improvements || evalResult.suggestions || [
                'Add more specific examples',
                'Be more concise in your response',
              ],
            },
            detailedTips: 'locked',
          };
        } else {
          throw new Error('Interview evaluation failed');
        }
      } else if (activeTab === 'ielts') {
        // For IELTS, evaluate speaking response
        const evalResponse = await fetch(`${API_BASE}/api/interview/evaluate-answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobTitle: 'IELTS Speaking Test',
            question: selectedPrompt.text,
            answer: transcribedText,
          }),
        });

        if (evalResponse.ok) {
          const evalResult = await evalResponse.json();
          const bandScore = calculateIELTSBand(evalResult.overallScore || 70);
          analysisData = {
            type: 'ielts',
            overallScore: bandScore,
            bandScore: bandScore,
            pronunciation: Math.min(9, Math.round((evalResult.overallScore || 70) / 11)),
            fluency: Math.min(9, Math.round(((evalResult.overallScore || 70) - 5) / 11)),
            vocabulary: Math.min(9, Math.round(((evalResult.overallScore || 70) + 5) / 12)),
            grammar: Math.min(9, Math.round((evalResult.overallScore || 70) / 12)),
            coherence: Math.min(9, Math.round(((evalResult.overallScore || 70) + 10) / 12)),
            feedback: evalResult.feedback || evalResult.summary || 'Your response was evaluated.',
            transcribedText,
            strengths: evalResult.strengths || [
              'Addressed the topic',
              'Provided some details',
            ],
            improvements: evalResult.improvements || evalResult.suggestions || [
              'Expand on your examples',
              'Use more varied vocabulary',
            ],
            detailedExplanation: 'locked',
          };
        } else {
          throw new Error('IELTS evaluation failed');
        }
      }

      setAnalysisResult(analysisData);
      setStep('result');
    } catch (error) {
      console.error('Error analyzing speech:', error);
      const message = error instanceof Error ? error.message : 'Something went wrong';
      setAnalysisError(message);
      setAudioBlob(null);
      setRecordingTime(0);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateSpeechFeedback = (proAnalysis: any): string => {
    if (!proAnalysis) return 'Good effort! Keep practicing to improve.';

    const score = proAnalysis.pronunciationScore || 75;
    if (score >= 90) return 'Excellent pronunciation! Your speech is very clear and natural.';
    if (score >= 80) return 'Great job! Your pronunciation is clear with minor areas for improvement.';
    if (score >= 70) return 'Good effort! Focus on word stress and clearer consonant sounds.';
    if (score >= 60) return 'Keep practicing! Pay attention to vowel sounds and speaking pace.';
    return 'Nice start! Try speaking more slowly and focus on each word clearly.';
  };

  const calculateIELTSBand = (score: number): number => {
    if (score >= 90) return 9;
    if (score >= 80) return 8;
    if (score >= 70) return 7;
    if (score >= 60) return 6.5;
    if (score >= 50) return 6;
    if (score >= 40) return 5.5;
    return 5;
  };


  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingEmail(true);

    try {
      // Save lead to backend (fire and forget)
      fetch(`${API_BASE}/api/quick-test-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          testType: activeTab,
          score: analysisResult?.overallScore,
        }),
      }).catch(() => {
        // Ignore errors - we still want to redirect
      });
    } catch (error) {
      console.error('Error submitting email:', error);
    }

    // Mark quick test as shown in localStorage
    localStorage.setItem('litrlly_quick_test_shown', 'true');

    // Redirect to login/signup with email parameter
    setIsSubmittingEmail(false);
    onClose();
    navigate(`/login?email=${encodeURIComponent(email)}`);
  };

  const resetTest = () => {
    setStep('select');
    setAudioBlob(null);
    setAnalysisResult(null);
    setAnalysisError(null);
    setRecordingTime(0);
  };

  const handleClose = () => {
    resetTest();
    onClose();
  };

  const renderSpeechResult = () => (
    <>
      <div className="text-center mb-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl font-bold text-white">{analysisResult.overallScore}</span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Your Score</h3>
        <p className="text-gray-600">{analysisResult.feedback}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{analysisResult.pronunciation}%</div>
          <div className="text-sm text-gray-500">Pronunciation</div>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{analysisResult.fluency}%</div>
          <div className="text-sm text-gray-500">Fluency</div>
        </div>
      </div>
    </>
  );

  const renderIELTSResult = () => (
    <>
      <div className="text-center mb-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl font-bold text-white">{analysisResult.bandScore}</span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Estimated Band Score</h3>
        <p className="text-gray-600 text-sm">{analysisResult.feedback}</p>
      </div>

      {/* IELTS Criteria Breakdown */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-emerald-600">{analysisResult.fluency}</div>
          <div className="text-xs text-gray-500">Fluency & Coherence</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-teal-600">{analysisResult.vocabulary}</div>
          <div className="text-xs text-gray-500">Vocabulary</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-blue-600">{analysisResult.grammar}</div>
          <div className="text-xs text-gray-500">Grammar</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-purple-600">{analysisResult.pronunciation}</div>
          <div className="text-xs text-gray-500">Pronunciation</div>
        </div>
      </div>

      {/* Strengths */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Strengths</h4>
        <ul className="space-y-1">
          {analysisResult.strengths?.map((s: string, i: number) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Areas to Improve */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Areas to Improve</h4>
        <ul className="space-y-1">
          {analysisResult.improvements?.slice(0, 2).map((s: string, i: number) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
              </svg>
              {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Locked Detailed Explanation */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-4 border border-emerald-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 text-sm">Detailed Band Score Explanation</h4>
            <p className="text-xs text-gray-600">Get word-by-word analysis and specific tips to reach Band 7+</p>
          </div>
        </div>
        <Link
          to="/login"
          className="mt-3 block w-full py-2 text-center text-sm font-medium text-emerald-700 bg-white rounded-lg border border-emerald-300 hover:bg-emerald-50 transition-colors"
        >
          Sign up free to unlock
        </Link>
      </div>
    </>
  );

  const renderInterviewResult = () => (
    <>
      <div className="text-center mb-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl font-bold text-white">{analysisResult.overallScore}</span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Interview Score</h3>
        <p className="text-gray-600 text-sm">{analysisResult.feedback}</p>
      </div>

      {/* Score Breakdown */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-purple-600">{analysisResult.pronunciation}%</div>
          <div className="text-xs text-gray-500">Pronunciation</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-pink-600">{analysisResult.fluency}%</div>
          <div className="text-xs text-gray-500">Fluency</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-indigo-600">{analysisResult.contentRelevance}%</div>
          <div className="text-xs text-gray-500">Content Relevance</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-blue-600">{analysisResult.confidence}%</div>
          <div className="text-xs text-gray-500">Confidence</div>
        </div>
      </div>

      {/* Answer Analysis */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Answer Analysis</h4>
        <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
          <p className="text-gray-600"><span className="font-medium text-gray-800">Relevance:</span> {analysisResult.answerAnalysis?.relevance}</p>
          <p className="text-gray-600"><span className="font-medium text-gray-800">Structure:</span> {analysisResult.answerAnalysis?.structure}</p>
        </div>
      </div>

      {/* Quick Improvements */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Quick Improvements</h4>
        <ul className="space-y-1">
          {analysisResult.answerAnalysis?.improvements?.slice(0, 2).map((s: string, i: number) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
              {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Locked Detailed Tips */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-4 border border-purple-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 text-sm">Expert Interview Tips</h4>
            <p className="text-xs text-gray-600">Get sample answers and industry-specific advice</p>
          </div>
        </div>
        <Link
          to="/login"
          className="mt-3 block w-full py-2 text-center text-sm font-medium text-purple-700 bg-white rounded-lg border border-purple-300 hover:bg-purple-50 transition-colors"
        >
          Sign up free to unlock
        </Link>
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8 text-white">
          <h2 className="text-2xl font-bold mb-2">Try It Free - No Sign Up Required</h2>
          <p className="text-blue-100">Experience our AI-powered feedback in just 1 minute</p>
        </div>

        {/* Tabs */}
        {step === 'select' && (
          <div className="p-6">
            <div className="flex gap-2 mb-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 p-3 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <span className="text-xl mb-1 block">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">{activeTabData.description}</h3>

              {/* Prompt Selection for IELTS and Interview */}
              {(activeTab === 'ielts' || activeTab === 'interview') && (
                <div className="mb-4">
                  <label className="block text-sm text-gray-600 mb-2">Choose a question:</label>
                  <div className="space-y-2">
                    {activeTabData.prompts.map((prompt) => (
                      <button
                        key={prompt.id}
                        onClick={() => setSelectedPromptId(prompt.id)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          selectedPromptId === prompt.id
                            ? 'border-blue-400 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium text-gray-800 text-sm">{prompt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Prompt Display */}
              <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
                <p className="text-sm text-gray-500 mb-1">
                  {activeTab === 'speech' ? 'Read this text:' : activeTab === 'ielts' ? 'Answer this question:' : 'Respond to this:'}
                </p>
                <p className="text-gray-800 font-medium">{selectedPrompt.text}</p>
              </div>

              <button
                onClick={() => setStep('record')}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
              >
                Start Quick Test
              </button>
            </div>
          </div>
        )}

        {/* Recording Step */}
        {step === 'record' && (
          <div className="p-6">
            <div className="text-center">
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-500 mb-1">
                  {activeTab === 'speech' ? 'Read this text:' : activeTab === 'ielts' ? 'Answer this question:' : 'Respond to this:'}
                </p>
                <p className="text-gray-800 font-medium">{selectedPrompt.text}</p>
              </div>

              {/* Error Message */}
              {analysisError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-red-700 font-medium">{analysisError}</p>
                    <p className="text-xs text-red-500 mt-1">Please try recording again.</p>
                  </div>
                  <button onClick={() => setAnalysisError(null)} className="text-red-400 hover:text-red-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Recording UI */}
              <div className="mb-6">
                {isRecording ? (
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mb-4 animate-pulse">
                      <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                    </p>
                    <p className="text-gray-500 mb-4">Recording... (max 1 minute)</p>
                    <button
                      onClick={stopRecording}
                      className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Stop Recording
                    </button>
                  </div>
                ) : audioBlob ? (
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-900 font-medium mb-4">Recording complete!</p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setAudioBlob(null);
                          setRecordingTime(0);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Re-record
                      </button>
                      <button
                        onClick={analyzeRecording}
                        disabled={isAnalyzing}
                        className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                      >
                        {isAnalyzing ? 'Analyzing...' : 'Get Feedback'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <button
                      onClick={startRecording}
                      className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mb-4 hover:bg-blue-200 transition-colors"
                    >
                      <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                        </svg>
                      </div>
                    </button>
                    <p className="text-gray-600">Tap to start recording</p>
                  </div>
                )}
              </div>

              <button
                onClick={resetTest}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to test selection
              </button>
            </div>
          </div>
        )}

        {/* Result Step */}
        {step === 'result' && analysisResult && (
          <div className="p-6">
            {activeTab === 'speech' && renderSpeechResult()}
            {activeTab === 'ielts' && renderIELTSResult()}
            {activeTab === 'interview' && renderInterviewResult()}

            <button
              onClick={() => setStep('email')}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
            >
              Continue Practicing - It's Free!
            </button>

            <button
              onClick={resetTest}
              className="w-full mt-3 py-2 text-gray-500 hover:text-gray-700 text-sm"
            >
              Try another test
            </button>
          </div>
        )}

        {/* Email Step */}
        {step === 'email' && (
          <div className="p-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Create Your Free Account</h3>
              <p className="text-gray-600 mb-6">
                Enter your email to get 5 free recordings daily and track your progress.
              </p>

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={isSubmittingEmail}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isSubmittingEmail ? 'Redirecting...' : 'Get Free Access'}
                </button>
              </form>

              <p className="mt-4 text-xs text-gray-500">
                You'll create a password on the next step.
              </p>

              <button
                onClick={handleClose}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
              >
                Maybe later
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickTestModal;
